import { Link } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import FAQItem from "./components/FAQItem";

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);

  const faqs = [
    { question: "How many locations can I create?", answer: "You can add as many locations to your QR code allergen menu as you like." },
    { question: "How many dishes can I add to my QR code menu?", answer: "You can add as many dishes to your QR code allergen menu as you like." },
    { question: "How quickly is my QR code activated?", answer: "As soon as you create your account and add a location, your QR code will be activated." },
    {
      question: "I need some help setting this up, what support do you provide?",
      answer:
        "Within your first 30 days we'll help you get set up, including creating locations, menus and ensuring you're ready and confident to use your QR code with your customers. If you have any specific needs with regards to this, just let us know!",
    },
    {
      question: "How and where can I use my QR code?",
      answer: "You receive a downloadable PDF which you can print, and a downloadable JPG which you can easily incorporate into other documents at whatever size you like.",
    },
    {
      question: "Does Natasha's Law apply to market stalls and street food vendors?",
      answer:
        "Yes. Natasha's Law (the Food Information (Amendment) (England) Regulations 2021) applies to all businesses selling prepacked for direct sale (PPDS) foods — including market stalls and street food vendors who prepare and package food on-site. If you sell food that is packaged at the same premises where it is sold, you must label it with the food name and a full ingredients list with allergens clearly emphasised.",
    },
    {
      question: "Is it legal to display allergen information using a QR code?",
      answer:
        "Yes. UK food law requires that allergen information is available to customers before they order, but does not specify the format it must be provided in. A QR code menu that clearly displays allergen information for each dish fully meets this requirement, and is widely accepted by environmental health officers as a reliable and easily updateable way to comply.",
    },
    {
      question: "What are the 14 major allergens I need to declare?",
      answer:
        "UK food law requires you to declare 14 major allergens: celery, cereals containing gluten (wheat, rye, barley, oats), crustaceans, eggs, fish, lupin, milk, molluscs, mustard, tree nuts (almonds, hazelnuts, walnuts, cashews, pecans, pistachios, macadamia nuts), peanuts, sesame, soybeans, and sulphur dioxide/sulphites. These must be clearly indicated on your menu or allergen information sheet.",
    },
    {
      question: "How much does tastebud.ee cost?",
      answer:
        "tastebud.ee offers two plans: monthly at £5 per month, or annual at £30 per year — saving 50% compared to paying monthly. Both plans include unlimited locations, unlimited menus, QR code generation, and setup support within your first 30 days. There is also a 30-day money-back guarantee.",
    },
  ];

  useEffect(() => {
    fetch("/blogPosts.json")
      .then((response) => response.json())
      .then((data) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const visible = data.filter((p) => !p.publishDate || new Date(p.publishDate) <= today);
        setPosts(visible);
        setPostsLoading(false);
      })
      .catch(() => setPostsLoading(false));
  }, []);

  return (
    <div className='text-center'>
      <Helmet>
        <title>QR Code Allergen Menus for Market Stalls &amp; Street Food Vendors | tastebud.ee</title>
        <meta name='description' content='Fun, simple and detailed QR code allergen menus for street vendors, market stall holders and small food venues. Get started for £5 per month.' />
        <link rel='canonical' href='https://tastebud.ee/' />
      </Helmet>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "tastebud.ee",
              "url": "https://tastebud.ee",
              "logo": "https://storage.googleapis.com/foodpop-marketing/homepage-assets/footer-icon.svg",
              "description": "QR code allergen menus for UK market stalls, street food vendors and small food venues.",
              "contactPoint": { "@type": "ContactPoint", "email": "hello@tastebud.ee", "contactType": "customer support" },
              "sameAs": ["https://twitter.com/richardstowey"],
            },
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "tastebud.ee",
              "url": "https://tastebud.ee",
              "description": "QR code allergen menus for UK market stalls, street food vendors and small food venues. Helps food businesses comply with UK allergen labelling law including Natasha's Law.",
            },
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "tastebud.ee",
              "applicationCategory": "BusinessApplication",
              "operatingSystem": "Web",
              "description": "Create and share QR code allergen menus for market stalls and street food venues. Compliant with UK allergen labelling requirements including Natasha's Law.",
              "offers": {
                "@type": "Offer",
                "price": "5.00",
                "priceCurrency": "GBP",
                "priceSpecification": {
                  "@type": "UnitPriceSpecification",
                  "price": "5.00",
                  "priceCurrency": "GBP",
                  "unitText": "month",
                },
              },
              "url": "https://tastebud.ee",
            },
          ]),
        }}
      />
      <div
        className='bg-middle bg-center bg-no-repeat bg-[length:1163px]'
        style={{ backgroundImage: "url('https://storage.googleapis.com/foodpop-marketing/homepage-assets/homepage-background.svg')" }}
      >
        <img
          src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/hero-image-optimised.png'
          className='mx-auto w-full sm:max-w-[580px] mt-10 mb-14 px-2'
          alt='A set of homepage screens, depicting what three different street vendor menus look like'
        />
        <h1 className='text-4xl max-w-[500px] mx-auto mb-6 text-[#161616] font-bold tracking-normal leading-snug'>Take away the pain of allergies and intolerances</h1>
      </div>
      <div className='text-center max-w-[640px] mx-auto'>
        <h2 className='text-xl mb-12 px-8'>
          Fun, simple and detailed <span className='underline underline-offset-4 decoration-red-500'>QR Code Menus</span> for street vendors, market stall holders and small food venues.
        </h2>
        {/* <h3>Share food ingredients and allergen information, making customers happy and expand your customer base. Optimise your time and optimise your serving capacity.</h3> */}
        <Link to='/account/register'>
          <button className='text-[#FFC701] bg-black rounded-xl font-bold px-6 py-3 mb-20'>Start your 30-day free trial</button>
        </Link>
      </div>
      <div className='py-20 text-white bg-[#161616]'>
        <h2 className='text-2xl mb-10 px-8 font-semibold'>Why use QR code menus?</h2>
        <div className='flex flex-wrap max-w-screen-lg mx-auto text-center'>
          <div className='w-full md:w-1/4 p-2 flex flex-col text-center my-10 md:my-2'>
            <img src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/lightening-red.png' alt='Red lightening symbol' className='mb-3 mx-auto' />
            <h3 className='text-xl mb-5 text-center'>Confidence</h3>
            <p className='max-w-xs mx-auto'>Give your customers the confidence they need to purchase</p>
          </div>
          <div className='w-full md:w-1/4 p-2 flex flex-col text-center my-10 md:my-2'>
            <img src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/lightening-white.png' alt='White lightening symbol' className='mb-3 mx-auto' />
            <h3 className='text-xl mb-5 text-center'>Easy to Use</h3>
            <p className='max-w-xs mx-auto'>Optimise your time using self-serve menus during peak trading periods</p>
          </div>
          <div className='w-full md:w-1/4 p-2 flex flex-col text-center my-10 md:my-2'>
            <img src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/lightening-yellow.png' alt='Yellow lightening symbol' className='mb-3 mx-auto' />
            <h3 className='text-xl mb-5 text-center'>Intuitive</h3>
            <p className='max-w-xs mx-auto'>Straightforward account management with an intuitive interface</p>
          </div>
          <div className='w-full md:w-1/4 p-2 flex flex-col text-center my-10 md:my-2'>
            <img src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/lightening-green.png' alt='Green lightening symbol' className='mb-3 mx-auto' />
            <h3 className='text-xl mb-5 text-center'>Flexible</h3>
            <p className='max-w-xs mx-auto'>Easily set up multiple businesses, menus and locations, each with their own vibe</p>
          </div>
        </div>
      </div>
      <div className='bg-[#F0F1F1] py-20 text-black'>
        <h2 className='text-2xl mb-4 px-8 font-semibold'>Street food allergen menus</h2>
        <p className='text-lg mb-10 px-8'>Take a look at how tastebud.ee is used by food vendors, either by following the link or scanning the code.</p>
        <div className='flex flex-wrap max-w-screen-lg mx-auto text-center'>
          <div className='w-full md:w-1/3 p-2 flex flex-col text-center my-10 md:my-2'>
            <a href='https://tastebud.ee/thetastythai/market-street' target='_blank' rel='noreferrer' title='Example menu for The Tasty Thai street vendor'>
              <h3 className='text-xl mb-5 text-center'>The Tasty Thai</h3>
              <img
                src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/tasty-thai-street-vendor.png'
                alt='The Tasty Thai street food market stall'
                className='mb-3 mx-auto rounded-full w-48'
              />
              <img src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/tasty-thai-qr.png' alt='QR code for The Tasty Thai' className='mb-3 mx-auto w-32' />
            </a>
          </div>
          <div className='w-full md:w-1/3 p-2 flex flex-col text-center my-10 md:my-2'>
            <a href='https://tastebud.ee/tokyokitchen/market-street' target='_blank' rel='noreferrer' title='Example menu for Tokyo Kitchen market food stall'>
              <h3 className='text-xl mb-5 text-center'>Tokyo Kitchen</h3>
              <img
                src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/tokyo-kitchen-market-food-stall.png'
                alt='Tokyo Kitchen street food venue'
                className='mb-3 mx-auto rounded-full w-48'
              />
              <img src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/tokyo-kitchen-qr.png' alt='QR code for Tokyo Kitchen' className='mb-3 mx-auto w-32' />
            </a>
          </div>
          <div className='w-full md:w-1/3 p-2 flex flex-col text-center my-10 md:my-2'>
            <a href='https://tastebud.ee/elchilango/market-street' target='_blank' rel='noreferrer' title='Example menu for El Chilango street food venue'>
              <h3 className='text-xl mb-5 text-center'>El Chilango</h3>
              <img
                src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/el-chilango-street-food-vendor.png'
                alt='El Chilango street food vendor'
                className='mb-3 mx-auto rounded-full w-48'
              />
              <img src='https://storage.googleapis.com/foodpop-marketing/homepage-assets/el-chilango-qr.png' alt='QR code for El Chilango' className='mb-3 mx-auto w-32' />
            </a>
          </div>
        </div>
      </div>

      {/* Frequently Asked Questions */}
      <div className='bg-[#7BC043] py-20 text-white'>
        <h2 className='text-2xl mb-4 px-8 font-semibold'>Frequently Asked Questions</h2>
        <p className='text-lg mb-10 px-8 mx-auto'>These are some of our most frequently asked questions. However, if we can't answer your questions here, get in touch!</p>
        <div className='flex flex-wrap max-w-screen-lg mx-auto text-center px-4'>
          {faqs.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: { "@type": "Answer", text: faq.answer },
              })),
            }),
          }}
        />
      </div>

      {/* Blog Post Section */}

      <div className='bg-[#FFF] py-20 text-black'>
        <h2 className='text-2xl mb-4 px-8 font-semibold'>The Importance of QR Code Allergen Menus</h2>
        <p className='text-lg mb-10 px-8 max-w-screen-lg mx-auto'>
          We believe in the importance of highlighting the benefits of displaying allergen's clearly and visibly, even for street traders and market stalls. Take a look at the following in-depth
          articles to help understand the importance of this topic more.
        </p>
        <div className='max-w-screen-lg mx-auto text-left'>
          {postsLoading && <div className='px-4'>Loading...</div>}
          {!postsLoading && posts.length === 0 && <div className='px-4'>No posts yet — check back soon.</div>}
          {posts.map((post) => (
            <div key={post.id} className='mb-2'>
              <h2 className='w-full px-2 pb-1 pt-2 text-xl mx-2'>
                <a href={`/blog/${post.id}`}>{post.title}</a>
              </h2>
              <p className='px-2 mx-2'>
                {post.date}: {post.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Closing CTA */}
      <div className='bg-[#161616] py-20 text-center'>
        <h2 className='text-white text-2xl font-semibold mb-4 px-8'>Ready to get started?</h2>
        <p className='text-gray-400 text-lg mb-8 max-w-[500px] mx-auto px-8'>Join market stall holders who use tastebud.ee to share allergen information quickly and confidently.</p>
        <Link to='/account/register'>
          <button className='text-black bg-[#FFC701] rounded-xl font-bold px-6 py-3'>Start your 30-day free trial</button>
        </Link>
      </div>
    </div>
  );
};

export default Home;
